import { buildCacheKeyForGetDetailed, buildCacheKeyForBodyDetailed } from './modules/keyBuilder';
import type { KeyBuildResult } from './modules/keyBuilder';
import { composeScopedKey, approxSize, makeCachedAxiosResponse, computeEvictionKeys } from './modules/helpers';

/**
 * AsyncCacheManager (IndexedDB-backed)
 *
 * Purpose
 * - Persistent, large-capacity cache for bulk JSON values.
 * - TTL-based expiration, LRU-like eviction when storage thresholds are hit.
 * - Namespaced key composition compatible with the synchronous CacheManager.
 *
 * Design
 * - Storage: IndexedDB (DB: `mp-cache`, store: `kv`).
 * - Record shape: { key, value, expiresAt, lastAccessed, size }.
 * - Size: approximated by JSON.stringify(value).length.
 * - Thresholds: uses navigator.storage.estimate() when available; otherwise
 *   compares sum of record sizes against a static limit.
 */

export type CacheValue = unknown;

interface KVRecord {
  key: string;
  value: CacheValue;
  expiresAt: number; // epoch ms
  lastAccessed: number; // epoch ms
  size: number; // bytes (approx.)
}

interface AsyncCacheOptions {
  namespace?: string;
  group?: string;
  defaultTtlMs?: number; // default 5 minutes
  /** Human string default TTL like '30s', '5m', '1h'. */
  defaultTtl?: string;
  // hard limit fallback when StorageManager.estimate is not available
  maxBytesFallback?: number; // default 100 MB
  // when threshold exceeded, evict this fraction of least recent records
  evictFraction?: number; // default 0.5
  // target usage ratio vs quota (only when estimate is available)
  maxUsageRatio?: number; // default 0.8 (80%)
  /** Enable hashing for large param/body key segments to avoid long keys. */
  hashLargeSegments?: boolean;
  /** Length threshold beyond which segments will be hashed (default 256). */
  hashThreshold?: number;
  /** When true, store sidecar key meta for UI/search/inspection. */
  emitMeta?: boolean;
}

/** Sidecar meta record shape for UI/search. */
interface MetaRecord {
  key: string;
  createdAt: number;
  lastUpdated: number;
  namespace?: string;
  group?: string;
  meta: KeyBuildResult;
}

export class AsyncCacheManager {
  private static DB_NAME = 'mp-cache';
  private static STORE = 'kv';
  private static META_STORE = 'meta';
  private static DB_VERSION = 2;
  /**
   * Shared IndexedDB connection promise.
   * All instances reuse this to avoid redundant open/upgrade cycles.
   */
  private static dbPromise: Promise<IDBDatabase> | null = null;

  /**
   * Global sweep coordinator
   * - lastSweepAt: timestamp of last eviction pass across any instance
   * - sweepInFlight: simple lock to avoid concurrent sweeps
   * - minSweepIntervalMs: minimum interval between sweeps (defaults to 60s)
   */
  private static lastSweepAt = 0;
  private static sweepInFlight = false;
  private static minSweepIntervalMs = 60_000;

  private namespace?: string;
  private group?: string;
  private defaultTtl: number;
  private maxBytesFallback: number;
  private evictFraction: number;
  private maxUsageRatio: number;
  private hashLargeSegments: boolean;
  private hashThreshold: number;
  private emitMeta: boolean;
  /** Special metrics record key (scoped) */
  private get metricsKey(): string { return composeScopedKey(this.namespace, this.group, '__metrics__'); }

  constructor(opts: AsyncCacheOptions = {}) {
    this.namespace = opts.namespace;
    this.group = opts.group;
    this.defaultTtl = ((): number => {
      if (typeof opts.defaultTtlMs === 'number') return opts.defaultTtlMs;
      const parsed = AsyncCacheManager.parseDurationToMs(opts.defaultTtl);
      return parsed ?? 300_000;
    })();
    this.maxBytesFallback = opts.maxBytesFallback ?? 100 * 1024 * 1024; // 100MB
    this.evictFraction = Math.min(Math.max(opts.evictFraction ?? 0.5, 0.1), 0.9);
    this.maxUsageRatio = Math.min(Math.max(opts.maxUsageRatio ?? 0.8, 0.5), 0.95);
  this.hashLargeSegments = !!opts.hashLargeSegments;
  this.hashThreshold = opts.hashThreshold ?? 256;
  this.emitMeta = !!opts.emitMeta;
  }

  /** Parse duration strings like '200ms', '30s', '5m', '1h', '1d' to ms. */
  private static parseDurationToMs(ttl?: string): number | undefined {
    if (!ttl) return undefined;
    const s = ttl.trim().toLowerCase();
    const m = s.match(/^([0-9]+)\s*(ms|s|m|h|d)?$/);
    if (!m) return undefined;
    const n = parseInt(m[1], 10);
    const unit = m[2] || 'ms';
    switch (unit) {
      case 'ms': return n;
      case 's': return n * 1000;
      case 'm': return n * 60_000;
      case 'h': return n * 3_600_000;
      case 'd': return n * 86_400_000;
      default: return undefined;
    }
  }

  // ---- Public API ----

  /** Compose a key like "ns:grp:key", omitting missing segments. */
  private composeKey(key: string): string {
  return composeScopedKey(this.namespace, this.group, key);
  }

  /**
   * Get a shared IndexedDB connection (creates once, then reuses).
   * Ensures object store exists on first upgrade.
   */
  private static getDb(): Promise<IDBDatabase> {
    if (this.dbPromise) return this.dbPromise;
    this.dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(AsyncCacheManager.DB_NAME, AsyncCacheManager.DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(AsyncCacheManager.STORE)) {
          db.createObjectStore(AsyncCacheManager.STORE, { keyPath: 'key' });
        }
        if (!db.objectStoreNames.contains(AsyncCacheManager.META_STORE)) {
          db.createObjectStore(AsyncCacheManager.META_STORE, { keyPath: 'key' });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    return this.dbPromise;
  }

  /**
   * Public accessor to the shared DB (for registry/UI introspection).
   */
  public static async openSharedDb(): Promise<IDBDatabase> {
    return this.getDb();
  }

  /**
   * Clear all cached entries across all namespaces/groups (entire app cache).
   * This wipes both the primary KV store and the sidecar META store.
   */
  public static async clearAll(): Promise<void> {
    try {
      const db = await this.openSharedDb();
      // Clear main KV store
      await new Promise<void>((resolve) => {
        const tx = db.transaction(AsyncCacheManager.STORE, 'readwrite');
        const store = tx.objectStore(AsyncCacheManager.STORE);
        const req = store.clear();
        req.onsuccess = () => resolve();
        req.onerror = () => resolve();
      });
      // Clear sidecar META store
      await new Promise<void>((resolve) => {
        const tx = db.transaction(AsyncCacheManager.META_STORE, 'readwrite');
        const store = tx.objectStore(AsyncCacheManager.META_STORE);
        const req = store.clear();
        req.onsuccess = () => resolve();
        req.onerror = () => resolve();
      });
    } catch {
      // Best effort: ignore errors
    }
  }

  /**
   * Instance wrapper to access the shared DB connection.
   */
  private openDb(): Promise<IDBDatabase> {
    return AsyncCacheManager.getDb();
  }

  /**
   * Open an object store transaction on the shared DB.
   * @param db Opened IndexedDB database
   * @param mode Transaction mode ('readonly' | 'readwrite')
   */
  private tx(db: IDBDatabase, mode: IDBTransactionMode): IDBObjectStore {
    return db.transaction(AsyncCacheManager.STORE, mode).objectStore(AsyncCacheManager.STORE);
  }

  /** Open a transaction on the meta sidecar store. */
  private metaTx(db: IDBDatabase, mode: IDBTransactionMode): IDBObjectStore {
    return db.transaction(AsyncCacheManager.META_STORE, mode).objectStore(AsyncCacheManager.META_STORE);
  }

  /** Get a value by key; updates lastAccessed on hit and enforces TTL. */
  async get<T = unknown>(key: string): Promise<T | null> {
    const db = await this.openDb();
    const store = this.tx(db, 'readwrite');
    const metaStore = this.metaTx(db, 'readwrite');
    const k = this.composeKey(key);
    const rec = await this.getRecord(store, k);
    if (!rec) return null;
    const now = Date.now();
    if (rec.expiresAt < now) {
      await this.deleteRecord(store, k);
      try { await new Promise<void>((resolve) => { const r = metaStore.delete(k); r.onsuccess = () => resolve(); r.onerror = () => resolve(); }); } catch {}
      await this.bumpMetric('misses');
      return null;
    }
    rec.lastAccessed = now;
    await this.putRecord(store, rec);
    await this.bumpMetric('hits');
    return rec.value as T;
  }

  /** Put a value with optional TTL; enforces capacity post-write. */
  async set(key: string, value: CacheValue, ttlMs?: number): Promise<void> {
    const db = await this.openDb();
    const store = this.tx(db, 'readwrite');
    const k = this.composeKey(key);
    const now = Date.now();
  const size = approxSize(value);
    const rec: KVRecord = {
      key: k,
      value,
      expiresAt: now + (ttlMs ?? this.defaultTtl),
      lastAccessed: now,
      size,
    };
    await this.putRecord(store, rec);
    await this.ensureCapacity(db);
  }

  /** Remove a single key or clear the namespace/group. */
  async clear(key?: string): Promise<void> {
    const db = await this.openDb();
    const store = this.tx(db, 'readwrite');
    const metaStore = this.metaTx(db, 'readwrite');
    if (key) {
      const k = this.composeKey(key);
      await this.deleteRecord(store, k);
      try { await new Promise<void>((resolve) => { const r = metaStore.delete(k); r.onsuccess = () => resolve(); r.onerror = () => resolve(); }); } catch {}
      return;
    }
    // Clear only keys matching current namespace/group (or all if none provided)
    const all = await this.scanKeys(store);
    const prefixParts = [this.namespace, this.group].filter(
      (v) => v != null && String(v).length > 0
    );
    const prefix = prefixParts.length ? prefixParts.join(':') + ':' : '';
    for (const k of all) {
      if (!prefix || k.startsWith(prefix)) {
        await this.deleteRecord(store, k);
  try { await new Promise<void>((resolve) => { const r = metaStore.delete(k); r.onsuccess = () => resolve(); r.onerror = () => resolve(); }); } catch {}
      }
    }
  }

  /** Return whether a non-expired entry exists for key. */
  async has(key: string): Promise<boolean> {
    const db = await this.openDb();
    const store = this.tx(db, 'readonly');
    const rec = await this.getRecord(store, this.composeKey(key));
    return !!rec && rec.expiresAt >= Date.now();
  }

  /** Fetch or compute and cache on miss. */
  async getOrSet<T = unknown>(
    key: string,
    producer: () => Promise<T>,
    ttlMs?: number
  ): Promise<T> {
    const hit = await this.get<T>(key);
    if (hit != null) return hit;
  await this.bumpMetric('misses');
    const val = await producer();
    await this.set(key, val as unknown as CacheValue, ttlMs);
    return val;
  }

  /**
   * Export a snapshot of this scope's cache for UI/inspection.
   * Returns { metrics, cache } where cache is an object keyed by full cache keys.
  *
  * metrics
  * - keys: number of keys in this scope
  * - totalSize: sum of approximate JSON sizes across values
  * - averageSize: average size per key
  * - hits: successful get hits in this scope
  * - misses: misses due to expiration or cold getOrSet
  *
  * cache
  * - key -> { value, size, lastAccessed, expiresAt }
  *
  * Note: sizes are approximate (JSON.stringify length) and meant for UI/relative comparisons.
   */
  public async exportSnapshot(): Promise<{ metrics: { keys: number; totalSize: number; averageSize: number; hits: number; misses: number }, cache: Record<string, any> } > {
    const db = await this.openDb();
    const store = this.tx(db, 'readonly');
    const metaStore = this.metaTx(db, 'readonly');
    const all: Record<string, any> = {};
    const prefixParts = [this.namespace, this.group].filter((v) => v != null && String(v).length > 0);
    const prefix = prefixParts.length ? prefixParts.join(':') + ':' : '';
    let totalSize = 0;
    let count = 0;
    await new Promise<void>((resolve, reject) => {
      const req = store.openCursor();
      req.onsuccess = () => {
        const cursor = req.result as IDBCursorWithValue | null;
        if (cursor) {
          const rec = cursor.value as KVRecord;
          if (!prefix || rec.key.startsWith(prefix)) {
            all[rec.key] = {
              value: rec.value,
              expiresAt: rec.expiresAt,
              lastAccessed: rec.lastAccessed,
              size: rec.size,
            };
            totalSize += rec.size || 0;
            count += 1;
          }
          cursor.continue();
        } else {
          resolve();
        }
      };
      req.onerror = () => resolve();
    });
    // read metrics record
    const metricsRec = await new Promise<any>((resolve) => {
      const r = metaStore.get(this.metricsKey);
      r.onsuccess = () => resolve(r.result || null);
      r.onerror = () => resolve(null);
    });
    const hits = metricsRec?.hits ?? 0;
    const misses = metricsRec?.misses ?? 0;
    const metrics = {
      keys: count,
      totalSize,
      averageSize: count ? Math.round(totalSize / count) : 0,
      hits,
      misses,
    };
    return { metrics, cache: all };
  }

  /** Increment a scoped metric counter in meta store. */
  private async bumpMetric(field: 'hits' | 'misses'): Promise<void> {
    try {
      const db = await this.openDb();
      const store = this.metaTx(db, 'readwrite');
      await new Promise<void>((resolve) => {
        const getReq = store.get(this.metricsKey);
        getReq.onsuccess = () => {
          const rec = (getReq.result as any) || { key: this.metricsKey, hits: 0, misses: 0, updatedAt: 0 };
          rec[field] = (rec[field] || 0) + 1;
          rec.updatedAt = Date.now();
          const putReq = store.put(rec);
          putReq.onsuccess = () => resolve();
          putReq.onerror = () => resolve();
        };
        getReq.onerror = () => {
          const rec = { key: this.metricsKey, hits: field === 'hits' ? 1 : 0, misses: field === 'misses' ? 1 : 0, updatedAt: Date.now() };
          const putReq = store.put(rec);
          putReq.onsuccess = () => resolve();
          putReq.onerror = () => resolve();
        };
      });
    } catch {
      // ignore
    }
  }

  // ---- Internals ----

  private getRecord(store: IDBObjectStore, key: string): Promise<KVRecord | null> {
    return new Promise((resolve, reject) => {
      const req = store.get(key);
      req.onsuccess = () => resolve((req.result as KVRecord) || null);
      req.onerror = () => reject(req.error);
    });
  }

  private putRecord(store: IDBObjectStore, rec: KVRecord): Promise<void> {
    return new Promise((resolve, reject) => {
      const req = store.put(rec);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  private deleteRecord(store: IDBObjectStore, key: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const req = store.delete(key);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  private scanKeys(store: IDBObjectStore): Promise<string[]> {
    return new Promise((resolve, reject) => {
      const keys: string[] = [];
      const req = store.openKeyCursor();
      req.onsuccess = () => {
        const cursor = req.result as IDBCursor | null;
        if (cursor) {
          keys.push(String(cursor.key));
          cursor.continue();
        } else {
          resolve(keys);
        }
      };
      req.onerror = () => reject(req.error);
    });
  }

  private scanMeta(db: IDBDatabase): Promise<Array<{ key: string; last: number; expiresAt: number; size: number }>> {
    const store = this.tx(db, 'readonly');
    return new Promise((resolve, reject) => {
      const out: Array<{ key: string; last: number; expiresAt: number; size: number }> = [];
      const req = store.openCursor();
      req.onsuccess = () => {
        const cursor = req.result as IDBCursorWithValue | null;
        if (cursor) {
          const rec = cursor.value as KVRecord;
          out.push({ key: rec.key, last: rec.lastAccessed ?? 0, expiresAt: rec.expiresAt, size: rec.size ?? 0 });
          cursor.continue();
        } else {
          resolve(out);
        }
      };
      req.onerror = () => reject(req.error);
    });
  }

  private async ensureCapacity(db: IDBDatabase): Promise<void> {
    // Global sweep gate: throttle eviction across instances
    const nowTs = Date.now();
    if (
      AsyncCacheManager.sweepInFlight ||
      nowTs - AsyncCacheManager.lastSweepAt < AsyncCacheManager.minSweepIntervalMs
    ) {
      return; // skip if another sweep ran recently
    }
    AsyncCacheManager.sweepInFlight = true;
    // Prefer origin-wide estimate when available
    try {
      if ('storage' in navigator && (navigator as any).storage?.estimate) {
        const est = await (navigator as any).storage.estimate();
        const usage: number = est.usage ?? 0;
        const quota: number = est.quota ?? 0;
        if (quota > 0 && usage / quota <= this.maxUsageRatio) return;
      }
    } catch {
      // ignore and fallback to per-DB meta sum
    }

    // Fallback: sum store sizes
    const meta = await this.scanMeta(db);
    const total = meta.reduce((acc, m) => acc + (m.size || 0), 0);
    if (total <= this.maxBytesFallback) {
      AsyncCacheManager.lastSweepAt = nowTs;
      AsyncCacheManager.sweepInFlight = false;
      return;
    }

    // Build eviction list via helper
    const now = Date.now();
    const toEvict = computeEvictionKeys(meta, now, this.evictFraction);
    // Use per-key transactions to avoid transaction inactivity when awaiting in a loop
    for (const k of toEvict) {
      // delete from main store
      await new Promise<void>((resolve) => {
        const tx1 = db.transaction(AsyncCacheManager.STORE, 'readwrite');
        const s1 = tx1.objectStore(AsyncCacheManager.STORE);
        const r1 = s1.delete(k);
        r1.onsuccess = () => resolve();
        r1.onerror = () => resolve();
      });
      // best-effort delete of sidecar meta
      await new Promise<void>((resolve) => {
        const tx2 = db.transaction(AsyncCacheManager.META_STORE, 'readwrite');
        const s2 = tx2.objectStore(AsyncCacheManager.META_STORE);
        const r2 = s2.delete(k);
        r2.onsuccess = () => resolve();
        r2.onerror = () => resolve();
      });
    }
    AsyncCacheManager.lastSweepAt = nowTs;
    AsyncCacheManager.sweepInFlight = false;
  }

  // ---- Axios helpers (optional, for plug-and-play use) ----
  // Import axios types lazily by using type-only imports to avoid runtime dependency.
  // eslint-disable-next-line @typescript-eslint/consistent-type-imports
  public async wrapAxiosGet<T = any>(
    axiosInstance: import('axios').AxiosInstance,
    url: string,
    config?: import('axios').AxiosRequestConfig,
    ttlMs?: number,
    key?: string
  ): Promise<import('axios').AxiosResponse<T>> {
    const detailed: KeyBuildResult = buildCacheKeyForGetDetailed(
      (config?.baseURL || axiosInstance.defaults.baseURL)?.toString(),
      url,
      config?.params as any,
      { hashLargeSegments: this.hashLargeSegments, hashThreshold: this.hashThreshold }
    );
  const cacheKey = this.composeKey(key || detailed.key);
  const hit = await this.get<T>(cacheKey);
  if (this.emitMeta) await this.upsertMetaRecord(cacheKey, detailed);
  if (hit != null) return makeCachedAxiosResponse<T>(hit);
    const res = await axiosInstance.get<T>(url, config);
    await this.set(cacheKey, res.data as unknown as CacheValue, ttlMs);
    return res;
  }

  // cached AxiosResponse factory moved to modules/response

  // stableStringify and key builders now live in modules/keyBuilder

  /**
   * POST/PUT wrappers (idempotent/safe only): cache by method+URL+params+body.
   */
  // eslint-disable-next-line @typescript-eslint/consistent-type-imports
  public async wrapAxiosPost<T = any>(
    axiosInstance: import('axios').AxiosInstance,
    url: string,
    data?: any,
    config?: import('axios').AxiosRequestConfig,
    ttlMs?: number,
    key?: string
  ): Promise<import('axios').AxiosResponse<T>> {
    const detailed: KeyBuildResult = buildCacheKeyForBodyDetailed(
      'POST',
      (config?.baseURL || axiosInstance.defaults.baseURL)?.toString(),
      url,
      data,
      config?.params as any,
      { hashLargeSegments: this.hashLargeSegments, hashThreshold: this.hashThreshold }
    );
  const cacheKey = this.composeKey(key || detailed.key);
  const hit = await this.get<T>(cacheKey);
  if (this.emitMeta) await this.upsertMetaRecord(cacheKey, detailed);
  if (hit != null) return makeCachedAxiosResponse<T>(hit);
    const res = await axiosInstance.post<T>(url, data, config);
    await this.set(cacheKey, res.data as unknown as CacheValue, ttlMs);
    return res;
  }

  // eslint-disable-next-line @typescript-eslint/consistent-type-imports
  public async wrapAxiosPut<T = any>(
    axiosInstance: import('axios').AxiosInstance,
    url: string,
    data?: any,
    config?: import('axios').AxiosRequestConfig,
    ttlMs?: number,
    key?: string
  ): Promise<import('axios').AxiosResponse<T>> {
    const detailed: KeyBuildResult = buildCacheKeyForBodyDetailed(
      'PUT',
      (config?.baseURL || axiosInstance.defaults.baseURL)?.toString(),
      url,
      data,
      config?.params as any,
      { hashLargeSegments: this.hashLargeSegments, hashThreshold: this.hashThreshold }
    );
  const cacheKey = this.composeKey(key || detailed.key);
  const hit = await this.get<T>(cacheKey);
  if (this.emitMeta) await this.upsertMetaRecord(cacheKey, detailed);
  if (hit != null) return makeCachedAxiosResponse<T>(hit);
    const res = await axiosInstance.put<T>(url, data, config);
    await this.set(cacheKey, res.data as unknown as CacheValue, ttlMs);
    return res;
  }


  /**
   * Upsert a sidecar meta record for UI/search, preserving createdAt on updates.
   */
  private async upsertMetaRecord(fullKey: string, meta: KeyBuildResult): Promise<void> {
    if (!this.emitMeta) return;
    const db = await this.openDb();
    const store = this.metaTx(db, 'readwrite');
    const now = Date.now();
    const rec: MetaRecord = {
      key: fullKey,
      createdAt: now,
      lastUpdated: now,
      namespace: this.namespace,
      group: this.group,
      meta,
    };
    await new Promise<void>((resolve) => {
      const getReq = store.get(fullKey);
      getReq.onsuccess = () => {
        const existing = getReq.result as MetaRecord | undefined;
        if (existing) {
          rec.createdAt = existing.createdAt;
        }
        const putReq = store.put(rec);
        putReq.onsuccess = () => resolve();
        putReq.onerror = () => resolve();
      };
      getReq.onerror = () => {
        const putReq = store.put(rec);
        putReq.onsuccess = () => resolve();
        putReq.onerror = () => resolve();
      };
    });
  }
}

export default AsyncCacheManager;
